import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TenantSettings } from '@rp/contracts'
import { useReadySession } from '../../session/useSession'

export const EDITABLE_SETTING_FIELDS = [
  'name',
  'suggestionMode',
  'primaryProvider',
  'secondaryProvider',
  'claimIdleMinutes',
  'answerabilityThreshold',
  'replyRequiresClaim',
] as const

export type EditableSettings = Pick<TenantSettings, (typeof EDITABLE_SETTING_FIELDS)[number]>

export const TENANT_SETTINGS_PATH = '/v1/tenant/settings'

export const settingsKeys = {
  all: ['settings'] as const,
  tenant: (tenantId: string) => ['settings', 'tenant', tenantId] as const,
  channels: (tenantId: string) => ['settings', 'channels', tenantId] as const,
  channelHealth: (tenantId: string) => ['settings', 'channel-health', tenantId] as const,
  members: (tenantId: string) => ['settings', 'members', tenantId] as const,
  metaApp: (tenantId: string) => ['settings', 'meta-app', tenantId] as const,
  aiCredentials: (tenantId: string) => ['settings', 'ai-credentials', tenantId] as const,
}

export const withDistinctProviders = (draft: EditableSettings): EditableSettings =>
  draft.secondaryProvider === draft.primaryProvider ? { ...draft, secondaryProvider: null } : draft

export const pickEditable = (settings: TenantSettings): EditableSettings =>
  withDistinctProviders({
    name: settings.name,
    suggestionMode: settings.suggestionMode,
    primaryProvider: settings.primaryProvider,
    secondaryProvider: settings.secondaryProvider,
    claimIdleMinutes: settings.claimIdleMinutes,
    answerabilityThreshold: settings.answerabilityThreshold,
    replyRequiresClaim: settings.replyRequiresClaim,
  })

export const useTenantSettings = () => {
  const { api, membership } = useReadySession()
  return useQuery({
    queryKey: settingsKeys.tenant(membership.tenantId),
    queryFn: async () => (await api.request(TenantSettings, { method: 'GET', path: TENANT_SETTINGS_PATH })).data,
  })
}

export const useUpdateTenantSettings = () => {
  const { api } = useReadySession()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: EditableSettings) =>
      (await api.request(TenantSettings, { method: 'PATCH', path: TENANT_SETTINGS_PATH, body: input })).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: settingsKeys.all }),
  })
}
